import type { AgenciesPageState } from './agencies-page.interface';
import type { AppState } from '../../../store';
import type { ConnectedProps } from 'react-redux';
import type { AgencyDto } from 'shared/dtos/agencies.dto';

import { bindActionCreators } from '@reduxjs/toolkit';
import { PermissionsContext } from 'contexts';
import React from 'react';
import { connect } from 'react-redux';
import {
  getAgenciesThunk,
  getAgencyThunk,
} from 'store/agencies/agencies.thunks';
import { openModal } from 'store/modals/modals.slice';
import { getPeopleThunk } from 'store/people/people.thunks';

import ReportService from '../../../services/reports.service';
import events from '../../../shared/constants/events';
import { MODAL_AGENCY } from '../../../shared/constants/modals';
import { vocabulary } from '../../../vocabulary/german';
import { CreateButton } from '../../buttons/create-button/create-button';
import AgenciesList from '../../lists/agencies-list/agencies-list';
import { Container } from '../../template/container/container';
import { NotFoundMessage } from '../../template/not-found-message/not-found-message';
import { PageHeader } from '../../template/page-header/page-header';
import { Spinner } from '../../template/spinner/spinner';

const mapStateToProps = (state: AppState) => ({
  agencies: state.agencies,
  isGetAgencyPending: state.agencies.isGetAgencyThunkPending,
  isUpdatePending: state.agencies.isUpdateAgencyThunkPending,
  isLoadingAgenciesList: state.agencies.isGetAgenciesThunkPending,
});
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getAgenciesThunk,
      getAgencyThunk,
      getPeopleThunk,
      openModal,
    },
    dispatch,
  );

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

class AgenciesPage extends React.Component<PropsFromRedux, AgenciesPageState> {
  static contextType = PermissionsContext;
  context!: React.ContextType<typeof PermissionsContext>;

  state = { locked: false };
  unlock = (): void => {
    this.setState({ locked: false });
  };

  componentDidMount(): void {
    this.props.getAgenciesThunk();
    this.props.getPeopleThunk();
    document.body.addEventListener(events.UNLOCK.name, this.unlock);
  }

  componentWillUnmount(): void {
    document.body.removeEventListener(events.UNLOCK.name, this.unlock);
  }

  handleCreate = () => {
    this.props.openModal({ name: MODAL_AGENCY });
  };

  handleSelect = (item: AgencyDto) => {
    if (item && item.id) {
      this.props.getAgencyThunk(item.id);
      this.props.openModal({ name: MODAL_AGENCY });
    }
  };

  downloadAgenciesReport = (type) => {
    if (this.context.permissions.printReport === true) {
      this.setState({ locked: true });
      const reportsService = new ReportService();
      reportsService.downloadAgenciesReport(type);
    }
  };

  createAgencyButton = (): JSX.Element | undefined => {
    if (this.context.permissions.createNew === true) {
      return (
        <CreateButton
          text={vocabulary.buttons.createNewAgency}
          onClick={this.handleCreate}
        />
      );
    }
  };

  render() {
    const agencies = this.props.agencies;

    if (this.props.isLoadingAgenciesList) {
      return <Spinner />;
    }

    if (agencies.list.length !== 0) {
      return (
        <React.Fragment>
          <div className={'page-content'}>
            <PageHeader
              title={vocabulary.labels.agencies}
              btnText={vocabulary.buttons.reports.downloadReportPdf}
              downloadReport={this.downloadAgenciesReport}
              btnTextDocx={vocabulary.buttons.reports.downloadReportDocx}
            />
            <Container>
              <AgenciesList
                agencies={agencies.list}
                onSelect={this.handleSelect}
              />
              {this.createAgencyButton()}
            </Container>
          </div>
          {this.state.locked && (
            <Spinner isFullSize message={vocabulary.preloader.waitForReport} />
          )}
        </React.Fragment>
      );
    }

    return (
      <>
        <NotFoundMessage
          message={`Nicht gefunden: Abteilungen konnten nicht gefunden werden.`}
        />
        {this.createAgencyButton()}
      </>
    );
  }
}

export default connector(AgenciesPage);
